import React from 'react';
import { withTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { ThemeProps } from '@theme/base';
import { TripTypes, TripTypeFromPrice } from '@model/common';
import { DestinationGroupComponent, ContentDestination } from '@model/content';
import { TestId } from '@components/test-ids';
import { DestinationTile } from '@components/destination/DestinationTile';
import { useResize } from '@components/hooks';
import { Carousel, getSlideDisplayCount } from '@components/common';

/* ***************** *
 *       Types       *
 * ***************** */
export interface DestinationsGroupProps extends DestinationGroupComponent {
  testId?: string;
  tripType?: TripTypes;
}

/* ***************** *
 *       Styles      *
 * ***************** */

const SeparatorRemover = styled.div(({ theme }: ThemeProps) => ({
  marginTop: -theme.custom.spacing.xxxLarge
}));

/* ******************************** *
 *    DestinationsGroupComponent    *
 * ******************************** */

export const DestinationsGroupComponent = (props: DestinationsGroupProps) => {
  const { title, destinations, tripTypeOverride, ctaTitle, ctaUrl, subTitle, testId, tripType } = props;

  /* *** LOGIC *** */
  // NOTE: we pick the destinations from the destinationData with paths matching the ones from the cms
  const numOfDestinations = destinations && destinations.length;

  if (!destinations || !numOfDestinations) {
    return <SeparatorRemover />;
  }

  const destinationTripType = (tripTypeOverride as TripTypes) || tripType || TripTypes.HOLIDAYS;
  const fromPriceField = TripTypeFromPrice[destinationTripType];
  const resize = useResize();
  const slideCount = getSlideDisplayCount(resize, destinations.length);
  const sizes = `${Math.round(100 / slideCount)}vw`;

  let isDragging = false;
  const setDragging = (newValue: boolean) => {
    isDragging = newValue;
  };

  /* *** RENDERERS *** */
  const renderDestinations: any = () =>
    destinations.map((destination: ContentDestination) => {
      return (
        <DestinationTile
          key={destination.path}
          tripType={destinationTripType}
          images={destination.image.images}
          path={destination.path}
          destination={destination.title}
          fromPrice={destination[fromPriceField] || destination.fromPrice}
          sizes={sizes}
          onClick={(e) => {
            if (isDragging) {
              e.preventDefault();
            }
          }}
        />
      );
    });

  const link = ctaTitle ? { label: ctaTitle, href: ctaUrl } : undefined;

  return (
    <Carousel
      testId={testId || TestId.destinationsGroup}
      title={title}
      subTitle={subTitle || undefined}
      link={link}
      setDragging={setDragging}
    >
      {renderDestinations()}
    </Carousel>
  );
};

export const DestinationsGroup = withTheme(DestinationsGroupComponent);
